import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal.tsx';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { PlusIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import PartyName from 'components/party/PartyName';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
    ACTIONS_CELL_CLASS,
    AddPayerRow,
    FOOTER_CELL_CLASS,
    MoveToTopButton,
    NUMERIC_CELL_CLASS,
    NUMERIC_HEAD_CLASS,
    PAYER_CELL_CLASS,
    PAYER_HEAD_CLASS,
    PAYER_TABLE_FRAME_CLASS,
    READ_ONLY_LINK_CELL_CLASS,
    ReadOnlyPayersNote,
    RemovePayerButton,
    ShareTotalIndicator,
    useRefusedDuplicatePick,
    ZERO_SHARE_ROW_CLASS,
} from 'pages/invoices/payers/PayerTableParts';
import { ShareInput, ShareRateDisplay } from 'pages/invoices/payers/ShareInput';
import { RefObject, useEffect, useRef } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { ContactMinDto } from 'types/contacts';
import { MatterPayerForm } from 'types/matters';
import { cn } from 'utils/cn';
import { indexedFieldName } from 'utils/formPath';
import { t } from 'utils/getTranslations';
import {
    isShareOutOfRange,
    newPayerRow,
    PAYER_SHARE_TOTAL,
    partyIdsOfOtherRows,
    payerShareTotal,
    shareRateValue,
    validatePayerRows,
    withoutPayer,
    withPayerOnTop,
} from '../matterPayers';
import { MatterFormState } from './useMatterForm';

const CELL_CLASS = cn(PAYER_CELL_CLASS, 'py-2');
const HEAD_CLASS = PAYER_HEAD_CLASS;
const CREATE_CONTACT_PERMISSIONS = ['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS'];
const NO_PARTY_IDS: number[] = [];

/** A payer at 0 % stays listed but receives no invoice. */
function hasNoInvoice(row: MatterPayerForm): boolean {
    return shareRateValue(row.shareRate) === 0;
}

/**
 * The party picker of one payer row; with a `label` it stands alone, as the single payer field. Its list leaves out
 * the parties the other rows hold, and a pick of one of them in its contact dialog is refused.
 */
function PayerPartyPicker({
    form,
    index,
    label,
    excludedPartyIds = NO_PARTY_IDS,
    onDuplicatePick,
}: {
    form: MatterFormState;
    index: number;
    label?: string;
    excludedPartyIds?: number[];
    onDuplicatePick?: () => void;
}) {
    const name = indexedFieldName('payers', index, 'party');
    const isOffered = (option: { id: number }) => !excludedPartyIds.includes(option.id);
    const pickParty = (fieldName: typeof name, party: ContactMinDto) => {
        if (isOffered(party)) {
            form.methods.setValue(fieldName, party);
        } else {
            onDuplicatePick?.();
        }
    };
    return (
        <ContactDynamicSelectPicker
            setValue={pickParty}
            control={form.methods.control}
            label={label}
            placeholder="matters.select-payer"
            name={name}
            PickerModalComponent={ContactPickerModal}
            paginated={{
                fetch: useLazyGetMinContactsListWithFiltersQuery,
                searchField: ['name', 'partyNumber'],
                transformOptions: (options: ContactMinDto[]) => options.filter(isOffered),
            }}
            noOptionsContent={
                <PermissionButton
                    permissions={CREATE_CONTACT_PERMISSIONS}
                    variant="contained"
                    color="secondary"
                    size="extraSmall"
                    onClick={() => form.openContactTypePicker(name)}
                >
                    <FormattedMessage id="contacts.add-new-contact" />
                </PermissionButton>
            }
            disabled={form.isLoadingNewClientData}
        />
    );
}

function AddMultiplePayersButton({ onClick }: { onClick: () => void }) {
    return (
        <Button
            type="button"
            {...buttonStyle({ variant: 'text', size: 'extraSmall' }, 'gap-1 self-start')}
            onClick={onClick}
        >
            <PlusIcon className="size-4" />
            <FormattedMessage id="matters.payers.add-multiple" />
        </Button>
    );
}

/** The list's label line, drawn as the form's other field labels are, with its help beside it. */
function PayersLabel() {
    const inSection = useInRecordSection();
    return (
        <span className="flex items-center gap-1.5">
            <span className={inSection ? FIELD_LABEL_CLASS : 'text-fg-secondary text-sm'}>
                <FormattedMessage id="matters.payers.title" />
            </span>
            <InfoTooltip text={['matters.payers.description', 'matters.payers.footnote']} />
        </span>
    );
}

function ShareTotalRow({ rows, columns }: { rows: MatterPayerForm[]; columns: number }) {
    const total = payerShareTotal(rows);
    return (
        <TableFooter className="border-t-0 bg-transparent">
            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                <TableCell className={FOOTER_CELL_CLASS}>
                    <FormattedMessage id="matters.payers.total" />
                </TableCell>
                <TableCell className={cn(FOOTER_CELL_CLASS, 'text-right')}>
                    <ShareTotalIndicator total={total} isComplete={total === PAYER_SHARE_TOTAL} />
                </TableCell>
                {columns > 2 ? <TableCell className={FOOTER_CELL_CLASS} colSpan={columns - 2} /> : null}
            </TableRow>
        </TableFooter>
    );
}

/** The stored list as those who may not manage payers see it: names as links and shares, the main payer first. */
function ReadOnlyPayers({
    rows,
    sectionRef,
}: {
    rows: MatterPayerForm[];
    sectionRef: RefObject<HTMLDivElement | null>;
}) {
    return (
        <div ref={sectionRef} className="flex flex-col gap-1">
            <PayersLabel />
            <div className={cn(PAYER_TABLE_FRAME_CLASS, 'overflow-x-auto')}>
                <Table>
                    <TableHeader className="[&_tr]:border-b-0">
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableHead className={HEAD_CLASS}>
                                <FormattedMessage id="matters.payers.payer" />
                            </TableHead>
                            <TableHead className={cn(NUMERIC_HEAD_CLASS, 'w-[120px]')}>
                                <FormattedMessage id="matters.payers.share" />
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) =>
                            row.party ? (
                                <TableRow
                                    key={row.party.id}
                                    className={cn(SIMPLE_TABLE_ROW_CLASS, hasNoInvoice(row) && ZERO_SHARE_ROW_CLASS)}
                                >
                                    <TableCell className={cn(CELL_CLASS, READ_ONLY_LINK_CELL_CLASS)}>
                                        <PartyName party={row.party} isLink variant="readonly-other" />
                                    </TableCell>
                                    <TableCell className={cn(NUMERIC_CELL_CLASS, 'py-2')}>
                                        <ShareRateDisplay value={shareRateValue(row.shareRate)} />
                                    </TableCell>
                                </TableRow>
                            ) : null
                        )}
                    </TableBody>
                    <ShareTotalRow rows={rows} columns={2} />
                </Table>
            </div>
            <ReadOnlyPayersNote messageId="matters.payers.read-only" />
        </div>
    );
}

/**
 * The payers of a case's invoices with their shares. A single payer is picked as it was before the payer list
 * existed; the table shows only while the list holds several payers, so removing all but one returns to the single
 * field. Without the permission to manage payers a stored list of several payers is read-only.
 */
export function MatterPayersFields({ form }: { form: MatterFormState }) {
    const {
        control,
        getValues,
        register,
        trigger,
        watch,
        formState: { errors, isSubmitted },
    } = form.methods;
    const { canManagePayers, hasStoredPayerList } = form;
    const { fields, append, replace } = useFieldArray({
        control,
        name: 'payers',
        keyName: 'formKey',
        rules: { validate: validatePayerRows },
    });
    const rows = useWatch({ control, name: 'payers' }) ?? [];
    const { isDuplicatePickRefused, refuseDuplicatePick } = useRefusedDuplicatePick(watch);
    const listError = errors.payers?.root;
    const shownListError = isDuplicatePickRefused ? { message: t('matters.payers.party-once') } : listError;
    const hasSeveralPayers = fields.length > 1;
    const isComplete = payerShareTotal(rows) === PAYER_SHARE_TOTAL;
    const hasShareOutOfRange = rows.some((row) => isShareOutOfRange(row.shareRate));
    const sectionRef = useRef<HTMLDivElement>(null);

    // The list's rule sits on the array, which no input stands for, so a change to a row would not re-run it and
    // the message would stay as the last save left it. A list operation reports no field name.
    useEffect(() => {
        if (!isSubmitted) {
            return;
        }
        const { unsubscribe } = watch((_, { name }) => {
            if (!name || name.startsWith('payers')) {
                void trigger('payers');
            }
        });
        return unsubscribe;
    }, [isSubmitted, trigger, watch]);

    // The form focuses the first field with an error on an invalid save; the list's rule has no input of its own,
    // so it is given this target, which brings the section into view and focuses its offending input.
    useEffect(() => {
        register('payers').ref({
            focus: () => {
                const section = sectionRef.current;
                if (!section) {
                    return;
                }
                section.scrollIntoView({ block: 'center', behavior: 'smooth' });
                // The inputs are marked invalid by the render the new error causes, which follows this call.
                requestAnimationFrame(() => {
                    const target =
                        section.querySelector<HTMLElement>('[aria-invalid="true"]') ?? section.querySelector('input');
                    target?.focus({ preventScroll: true });
                });
            },
        });
    }, [register]);

    if (!canManagePayers && hasStoredPayerList) {
        return <ReadOnlyPayers rows={rows} sectionRef={sectionRef} />;
    }

    const addPayer = () => append(newPayerRow(getValues('payers')));

    if (!hasSeveralPayers) {
        return (
            <div ref={sectionRef} className="flex flex-col gap-1">
                <PayerPartyPicker form={form} index={0} label="matters.payer" />
                {canManagePayers ? <AddMultiplePayersButton onClick={addPayer} /> : null}
            </div>
        );
    }

    const moveToTop = (index: number) => replace(withPayerOnTop(getValues('payers'), index));
    const removePayer = (index: number) => replace(withoutPayer(getValues('payers'), index));

    return (
        <div ref={sectionRef} className="flex flex-col gap-1">
            <PayersLabel />
            <div className={cn(PAYER_TABLE_FRAME_CLASS, 'overflow-x-auto')}>
                <Table>
                    <TableHeader className="[&_tr]:border-b-0">
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableHead className={HEAD_CLASS}>
                                <FormattedMessage id="matters.payers.payer" />
                            </TableHead>
                            <TableHead className={cn(NUMERIC_HEAD_CLASS, 'w-[120px]')}>
                                <FormattedMessage id="matters.payers.share" />
                            </TableHead>
                            <TableHead className={cn(HEAD_CLASS, 'w-24')} />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {fields.map((field, index) => {
                            const row = rows[index] ?? field;
                            const payerName = row.party?.name ?? t('matters.payers.payer');
                            return (
                                <TableRow
                                    key={field.formKey}
                                    className={cn(SIMPLE_TABLE_ROW_CLASS, hasNoInvoice(row) && ZERO_SHARE_ROW_CLASS)}
                                >
                                    <TableCell className={cn(CELL_CLASS, 'min-w-[260px]')}>
                                        <PayerPartyPicker
                                            form={form}
                                            index={index}
                                            excludedPartyIds={partyIdsOfOtherRows(rows, index)}
                                            onDuplicatePick={refuseDuplicatePick}
                                        />
                                    </TableCell>
                                    <TableCell className={cn(NUMERIC_CELL_CLASS, 'py-2')}>
                                        <ShareInput
                                            control={control}
                                            name={indexedFieldName('payers', index, 'shareRate')}
                                            kind="rate"
                                            className="w-[120px]"
                                            ariaLabel={t('matters.payers.share-of-payer', { name: payerName })}
                                            isInvalid={
                                                !!listError &&
                                                (hasShareOutOfRange ? isShareOutOfRange(row.shareRate) : !isComplete)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell className={ACTIONS_CELL_CLASS}>
                                        <span className="inline-flex items-center gap-1">
                                            {index > 0 ? (
                                                <MoveToTopButton
                                                    label={t('matters.payers.move-payer-to-top', { name: payerName })}
                                                    onClick={() => moveToTop(index)}
                                                />
                                            ) : null}
                                            <RemovePayerButton
                                                label={t('matters.payers.remove-payer', { name: payerName })}
                                                onClick={() => removePayer(index)}
                                            />
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <AddPayerRow columnCount={3} onClick={addPayer} />
                    </TableBody>
                    <ShareTotalRow rows={rows} columns={3} />
                </Table>
            </div>
            {shownListError ? <FieldError errors={[shownListError]} /> : null}
        </div>
    );
}
